"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar, ComposedChart, CartesianGrid, Line,
  XAxis, YAxis, Legend, Tooltip, ResponsiveContainer,
} from "recharts";
import { CircleHelp, Download, ChevronDown } from "lucide-react";
import {
  Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import axiosInstance from "@/api/axios";
import { ChartContainer } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Grey palette — bar fill vs. the trend line stroke.
const BAR_COLOR  = "#9CA3AF"; // gray-400
const LINE_COLOR = "#4B5563"; // gray-600

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Picks a "nice" step (1, 2, 2.5, 5, 10 × 10^n) so all y-axis ticks
// are evenly spaced round numbers, then returns a max that's a
// clean multiple of that step plus the tick array itself.
function getNiceScale(maxValue, tickCount = 5) {
  if (!maxValue || maxValue <= 0) {
    return { niceMax: tickCount, ticks: Array.from({ length: tickCount + 1 }, (_, i) => i) };
  }
  const rawStep = maxValue / tickCount;
  const exponent = Math.floor(Math.log10(rawStep));
  const fraction = rawStep / Math.pow(10, exponent);
  const niceFraction =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  const step = niceFraction * Math.pow(10, exponent);
  const niceMax = Math.ceil(maxValue / step) * step;
  const ticks = [];
  for (let v = 0; v <= niceMax + step / 2; v += step) ticks.push(Number(v.toFixed(6)));
  return { niceMax, ticks };
}

const CustomTooltip = ({ active, payload, label, mode, barKey, lineKey }) => {
  if (!active || !payload?.length) return null;

  // Two series can be present (Bar + Line) since they use separate data
  // arrays — find the Bar's entry explicitly by dataKey rather than
  // trusting payload[0], which can land on the Line's entry instead.
  const barEntry = payload.find((p) => p.dataKey === barKey);
  if (!barEntry) return null;

  const point = barEntry.payload;
  const value = barEntry.value;
  const avgValue = point[lineKey];

  return (
    <div className="rounded-lg border bg-background p-3 shadow-md text-sm min-w-[170px]">
      <p className="font-semibold mb-2">{label}</p>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Contaminated</span>
        <span className="font-medium">
          {mode === "kg" ? `${Number(value).toFixed(3)} kg` : `${Number(value).toFixed(1)}%`}
        </span>
      </div>
      {avgValue != null && (
        <div className="flex items-center justify-between gap-4 mt-1">
          <span className="text-muted-foreground">Moving average</span>
          <span className="font-medium">
            {mode === "kg" ? `${Number(avgValue).toFixed(3)} kg` : `${Number(avgValue).toFixed(1)}%`}
          </span>
        </div>
      )}
      <div className="border-t mt-2 pt-2 text-xs text-muted-foreground">
        Total items: <span className="font-medium text-foreground">{point.total_contaminated_items ?? 0}</span>
      </div>
    </div>
  );
};

export function ContaminationTrendChart({
  className,
  title,
  bins = [],
  binTypes = {},
  timeRange,
  customDateRange = [{ startDate: null, endDate: null, key: "selection" }],
  isCustomDateRange = false,
  ...props
}) {
  const { t } = useTranslation();
  const exportRef = useRef(null);
  const [chartData, setChartData] = useState([]); // real periods only — feeds the bars
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState(null);
  const [mode, setMode]           = useState("kg");

  const chartConfig = {
    contaminated: { label: "Contaminated",   color: BAR_COLOR },
    trend:        { label: "Moving average", color: LINE_COLOR },
  };

  useEffect(() => {
    if (!bins || bins.length === 0) return;
    if (!timeRange && !isCustomDateRange) return;

    setIsLoading(true);
    setError(null);
    setChartData([]);

    const p = new URLSearchParams();
    bins.forEach((b) => p.append("bin", b));
    p.set("period", timeRange || "year");

    if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
      const s = customDateRange[0].startDate;
      const e = customDateRange[0].endDate;
      p.set("start_date", s instanceof Date ? toISO(s) : toISO(new Date(s)));
      p.set("end_date",   e instanceof Date ? toISO(e) : toISO(new Date(e)));
    }

    axiosInstance
      .get(`/contamination-timeseries/?${p.toString()}`)
      .then((res) => {
        const data = res.data;
        if (!Array.isArray(data)) { setError("Unexpected response"); return; }

        const shaped = data.map((item) => ({
          period:                    item.period,
          contamination_rate:        item.contamination_rate ?? 0,
          total_contaminated_kg:     item.total_contaminated_kg ?? 0,
          // Contaminated items for THIS period only — API already returns
          // this as `contaminated_items` (see ContaminationTimeSeriesAPIView).
          // NOT the bin's total item count (that's `total_items`).
          total_contaminated_items:  item.contaminated_items ?? 0,
        }));

        // Cumulative (running) average up to and including each point:
        // point i's average = sum of values[0..i] / (i+1) — i.e. all
        // previous points summed, divided by how many there are so far.
        // Point A averages with itself, so the line has a value at the
        // very first bar.
        let kgSum = 0;
        let pctSum = 0;
        const withAvg = shaped.map((d, i) => {
          kgSum  += d.total_contaminated_kg;
          pctSum += d.contamination_rate;
          return {
            ...d,
            mid_kg:  kgSum  / (i + 1),
            mid_pct: pctSum / (i + 1),
          };
        });

        setChartData(withAvg);
      })
      .catch(() => setError("Failed to load data"))
      .finally(() => setIsLoading(false));
  }, [
    timeRange,
    isCustomDateRange,
    JSON.stringify(bins),
    customDateRange?.[0]?.startDate instanceof Date
      ? customDateRange[0].startDate.toDateString()
      : String(customDateRange?.[0]?.startDate ?? ""),
    customDateRange?.[0]?.endDate instanceof Date
      ? customDateRange[0].endDate.toDateString()
      : String(customDateRange?.[0]?.endDate ?? ""),
  ]);

  const barKey = mode === "kg" ? "total_contaminated_kg" : "contamination_rate";
  const lineKey = mode === "kg" ? "mid_kg" : "mid_pct";

  const lineData = useMemo(() => {
    const N = chartData.length;
    if (N === 0) return [];

    return chartData.map((d, i) => ({
      ...d,
      _pos: (i + 0.5) / N,
    }));
  }, [chartData]);

  // Nice, evenly-spaced y-axis: max and ticks are derived together so
  // steps are always a round increment (e.g. every 20, every 30, etc).
  const { niceMax: yMax, ticks: yTicks } = (() => {
    if (!chartData.length) return { niceMax: "auto", ticks: undefined };
    const max = Math.max(...chartData.map((d) => d[barKey] ?? 0));
    return getNiceScale(max * 1.15);
  })();

  const handleExportCSV = () => {
    if (!chartData.length) return;
    const headers = ["Period", "Contamination %", "Contaminated kg", "Moving Average", "Total Items"];
    const rows = chartData.map((d) =>
      [
        d.period,
        d.contamination_rate?.toFixed(2)    ?? "",
        d.total_contaminated_kg?.toFixed(3) ?? "",
        mode === "kg" ? (d.mid_kg?.toFixed(3) ?? "") : (d.mid_pct?.toFixed(2) ?? ""),
        d.total_contaminated_items ?? "",
      ].join(",")
    );
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href     = URL.createObjectURL(blob);
    link.download = `contamination_trend_${timeRange || "data"}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const handleExportPNG = () => {
    if (!exportRef.current) return;
    const controls = exportRef.current.querySelector(".export-controls");
    if (controls) controls.style.display = "none";
    import("html2canvas").then((h) => {
      h.default(exportRef.current, { scale: 2, useCORS: true }).then((canvas) => {
        const link = document.createElement("a");
        link.download = `contamination_trend_${timeRange || "data"}.png`;
        link.href     = canvas.toDataURL("image/png");
        link.click();
      }).finally(() => { if (controls) controls.style.display = ""; });
    });
  };

  return (
    <div
      ref={exportRef}
      className={cn(
        "lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section border-0 shadow-none",
        className
      )}
      {...props}
    >
      <div className="flex flex-wrap justify-between items-start gap-2 pb-2">
        <div>
          <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
            {title || t("contamination.titleTrend")}
          </h4>
          {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {new Date(customDateRange[0].startDate).toLocaleDateString()} –{" "}
              {new Date(customDateRange[0].endDate).toLocaleDateString()}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 export-controls">
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1 border border-gray-200 dark:border-slate-700">
            <button
              onClick={() => setMode("kg")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mode === "kg"
                  ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >KG</button>
            <button
              onClick={() => setMode("percentage")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mode === "percentage"
                  ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >%</button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm"
                      className="text-muted-foreground hover:text-primary"
                      disabled={!chartData.length || isLoading}>
                <Download className="h-4 w-4 mr-1" />
                Export
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV}>Download as CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPNG}>Download as PNG</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <CircleHelp className="h-5 w-5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                Bar height = contamination per period. The dashed line is a running average — each point averages all periods up to and including it — plotted through each bar's midpoint, not its top.
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{ width: "100%", height: "100%" }}>
        {isLoading ? (
          // Skeleton — y-axis ticks, gridlines, six bars at varying
          // heights, a dashed line tracing roughly through each bar's
          // midpoint (standing in for the moving-average line), x-axis
          // ticks, and a two-item legend — matching the real combo chart.
          <div className="flex flex-col h-full w-full motion-safe:animate-pulse" aria-busy="true">
            <span className="sr-only" role="status">Loading…</span>

            <div className="flex-1 flex">
              <div className="flex flex-col justify-between py-2 pr-2 w-12">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-2 w-full rounded bg-gray-200 dark:bg-slate-700" />
                ))}
              </div>

              <div className="flex-1 relative">
                <div className="absolute inset-0 flex flex-col justify-between py-2">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-px w-full bg-gray-200 dark:bg-slate-700" />
                  ))}
                </div>
                <div className="absolute inset-0 flex items-end justify-around px-4 pb-1">
                  {[30, 55, 40, 65, 48, 58].map((h, i) => (
                    <div
                      key={i}
                      className="w-8 rounded-t bg-gray-200 dark:bg-slate-700"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true">
                  <path
                    d="M8,30 L25,22 L42,26 L58,16 L75,20 L92,15"
                    fill="none"
                    className="stroke-gray-400 dark:stroke-slate-500"
                    strokeWidth="1.5"
                    strokeDasharray="4 3"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              </div>
            </div>

            <div className="flex justify-around px-4 pt-2">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-2 w-8 rounded bg-gray-200 dark:bg-slate-700" />
              ))}
            </div>

            <div className="flex justify-center gap-4 pt-3">
              <div className="flex items-center gap-1.5">
                <div className="h-2.5 w-2.5 rounded-sm bg-gray-200 dark:bg-slate-700" />
                <div className="h-2 w-20 rounded bg-gray-200 dark:bg-slate-700" />
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-0.5 w-4 bg-gray-200 dark:bg-slate-700" />
                <div className="h-2 w-24 rounded bg-gray-200 dark:bg-slate-700" />
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-destructive">{error}</div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[400px]">
            <div className="text-muted-foreground text-center">
              <p className="mb-2 text-lg">No data available</p>
              <p className="text-sm">Select a time range and bins to view contamination trends</p>
            </div>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 28, right: 30, left: 0, bottom: 20 }}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />

                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 11 }}
                  tickLine={{ stroke: "hsl(var(--border))" }}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  interval="preserveStartEnd"
                  padding={{ left: 0, right: 0 }}
                />

                {/* Hidden numeric axis the Line rides on — independent of
                    the categorical bar axis, spans exactly [0,1] with no
                    padding, so the line's edge anchors sit flush at the
                    true plot corners. */}
                <XAxis
                  xAxisId="lineAxis"
                  dataKey="_pos"
                  type="number"
                  domain={[0, 1]}
                  padding={{ left: 0, right: 0 }}
                  hide
                />

                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={{ stroke: "hsl(var(--border))" }}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  tickFormatter={(v) => mode === "kg" ? `${v} kg` : `${v}%`}
                  domain={[0, yMax]}
                  ticks={yTicks}
                  width={mode === "kg" ? 64 : 44}
                />
                <Tooltip
                  content={<CustomTooltip mode={mode} barKey={barKey} lineKey={lineKey} />}
                  cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
                />
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={{ fontSize: 12, paddingTop: 14 }}
                  payload={[
                    { value: "Contaminated",   type: "square", color: BAR_COLOR },
                    { value: "Moving average", type: "line",   color: LINE_COLOR },
                  ]}
                />

                <Bar
                  dataKey={barKey}
                  fill={BAR_COLOR}
                  name="Contaminated"
                  maxBarSize={60}
                  radius={[2, 2, 0, 0]}
                />

                <Line
                  data={lineData}
                  xAxisId="lineAxis"
                  dataKey={lineKey}
                  type="monotone"
                  stroke={LINE_COLOR}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={{ r: 3, fill: LINE_COLOR, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                  name="Moving average"
                  isAnimationActive={false}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </div>
    </div>
  );
}

export default ContaminationTrendChart;